import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { isNilOrEmpty, useAxios } from 'packages/core'
import { useCurrentPerson } from 'hooks'
import { useNotification } from 'packages/eid-ui'
import { useTranslation } from 'react-i18next'

export const CUSTOM_CATEGORIES_KEY = 'CUSTOM_CATEGORIES_KEY'
export const ALL_SSO_APPLICATIONS_KEY = 'ALL_SSO_APPLICATIONS_KEY'

export const useAllSSOApplications = (queryData, enabled = true) => {
    const { sorting, ...restQueryData } = queryData

    const payload = {
        ...restQueryData,
    }
    if (!isNilOrEmpty(sorting?.sortOrder)) {
        payload.sortBy = sorting.sortBy
        payload.desc = sorting.sortOrder === 'desc'
    }

    const callApi = useAxios()
    return useQuery(
        [ALL_SSO_APPLICATIONS_KEY, queryData],
        () =>
            callApi({
                method: 'POST',
                url: `/api/ProtectedApplicationResource/SSOApplications`,
                data: payload,
            }),
        { enabled: Boolean(enabled) },
    )
}

export const useAllCustomCategories = () => {
    const { data: currentPerson } = useCurrentPerson()

    const callApi = useAxios()
    return useQuery([currentPerson?.id, CUSTOM_CATEGORIES_KEY], () =>
        callApi({
            method: 'GET',
            url: `/api/ProtectedApplicationResource/GetAllSsoCategoriesForUser`,
        }).then((res) =>
            res.data.map((item) => ({
                ...item,
                isCustomTab: true,
                title: item.name,
                route: `/${item.id}`,
            })),
        ),
    )
}

export const useCreateCustomCategory = () => {
    const callApi = useAxios()
    const { data: currentPerson } = useCurrentPerson()
    const { t } = useTranslation()
    const { showSuccessMessage, showWarningMessage } = useNotification()
    const queryClient = useQueryClient()

    return useMutation(
        (data) =>
            callApi({
                method: 'POST',
                url: '/api/ProtectedApplicationResource/AddCategory',
                data,
            }).catch((err) => showWarningMessage(t('Error'), err)),
        {
            onSuccess: (data) => {
                queryClient.invalidateQueries({
                    queryKey: [currentPerson?.id, CUSTOM_CATEGORIES_KEY],
                })
                showSuccessMessage(data?.data?.message)
            },
        },
    )
}

export const useUpdateCustomCategory = () => {
    const callApi = useAxios()
    const { data: currentPerson } = useCurrentPerson()
    const { t } = useTranslation()
    const { showSuccessMessage, showWarningMessage } = useNotification()
    const queryClient = useQueryClient()

    return useMutation(
        (data) =>
            callApi({
                method: 'PATCH',
                url: `/api/ProtectedApplicationResource/UpdateCategoryName`,
                data,
            }).catch((err) => showWarningMessage(t('Error'))),
        {
            onSuccess: (data) => {
                queryClient.invalidateQueries({
                    queryKey: [currentPerson?.id, CUSTOM_CATEGORIES_KEY],
                })
                showSuccessMessage(data?.data?.message)
            },
            onError: () => {
                showWarningMessage(t('Error'))
            },
        },
    )
}

export const useDeleteCategory = () => {
    const callApi = useAxios()
    const { data: currentPerson } = useCurrentPerson()
    const { t } = useTranslation()
    const { showSuccessMessage, showWarningMessage } = useNotification()
    const queryClient = useQueryClient()

    return useMutation(
        (data) =>
            callApi({
                method: 'DELETE',
                url: `/api/ProtectedApplicationResource/DeleteCategorybyCategoryId`,
                data,
            }).catch((err) => showWarningMessage(t('Error'))),
        {
            onSuccess: (data) => {
                queryClient.invalidateQueries({
                    queryKey: [currentPerson?.id, CUSTOM_CATEGORIES_KEY],
                })
                showSuccessMessage(data?.data?.message)
            },
            onError: () => {
                showWarningMessage(t('Error'))
            },
        },
    )
}

export const useAssignApplicationToCategory = () => {
    const callApi = useAxios()
    const { data: currentPerson } = useCurrentPerson()
    const { t } = useTranslation()
    const { showSuccessMessage, showWarningMessage } = useNotification()
    const queryClient = useQueryClient()

    return useMutation(
        (data) =>
            callApi({
                method: 'POST',
                url: `/api/ProtectedApplicationResource/assignCategoryToSSOApplication`,
                data,
            }).catch((err) => showWarningMessage(t('Error'))),
        {
            onSuccess: () => {
                queryClient.invalidateQueries({
                    queryKey: [currentPerson?.id, ALL_SSO_APPLICATIONS_KEY],
                })
                showSuccessMessage(t('ApplicationsAssignedSuccessfully'))
            },
            onError: () => {
                showWarningMessage(t('Error'))
            },
        },
    )
}

export const useRemoveApplicationFromCategory = () => {
    const callApi = useAxios()
    const { data: currentPerson } = useCurrentPerson()
    const { t } = useTranslation()
    const { showSuccessMessage, showWarningMessage } = useNotification()
    const queryClient = useQueryClient()

    return useMutation(
        (data) =>
            callApi({
                method: 'POST',
                url: `/api/ProtectedApplicationResource/removeCategoryFromSSOApplication`,
                data,
            }).catch((err) => showWarningMessage(t('Error'))),
        {
            onSuccess: () => {
                queryClient.invalidateQueries({
                    queryKey: [currentPerson?.id, ALL_SSO_APPLICATIONS_KEY],
                })
                showSuccessMessage(t('ApplicationsRemovedSuccessfully'))
            },
            onError: () => {
                showWarningMessage(t('Error'))
            },
        },
    )
}
